import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { RolloutDayKey, RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import { ReleasePlanDay } from '../../../../../redux/api/fymAPI.types';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { itemsCredits } from '../../studio/studioRules';
import { SocialPlatform } from '../../types';
import { PlanMakeNeeds, planMakeNeeds } from './planModel';
import { planMessages } from './planMessages';
import { planAssetName } from './rolloutDayCopy';
import { AddReleaseDrafts, useAddReleaseDrafts } from './useAddReleaseDrafts';

interface UsePlanGapActionsProps {
  accountId: number;
  catalogItemUuid: string;
  kind: RolloutKind;
  platforms: SocialPlatform[];
  /** The days that one "Add N drafts" click adds (the gap days, or the ticked push weeks). */
  days: ReleasePlanDay[];
}

export interface PlanGapActions {
  drafts: AddReleaseDrafts;
  /** What the click makes first, and its video credits. */
  needs: PlanMakeNeeds;
  credits: number;
  /** "Story is not made yet, so we add it as a Studio job (1 video credit)." Null when nothing is made. */
  makesLine: string | null;
  /** Adds the drafts of `days`. A short balance opens the packs and sends nothing. */
  addAll: () => void;
  /** "Add a draft" of one gap day. */
  addDay: (day: ReleasePlanDay) => void;
}

/**
 * The actions of the gap bar and of each gap day (designs f1, f2). Each click sends `makeMissing`,
 * so a kit asset that is not made yet becomes a Studio job first. The video credit check runs
 * before the click (owner rule 2026-10-06, `useVideoCreditGate`); images cost nothing.
 */
export function usePlanGapActions({
  accountId,
  catalogItemUuid,
  kind,
  platforms,
  days,
}: UsePlanGapActionsProps): PlanGapActions {
  const intl = useIntl();
  const drafts = useAddReleaseDrafts({ accountId, catalogItemUuid });
  const { canSpend } = useVideoCreditGate(accountId);
  const needs = useMemo(() => planMakeNeeds(days, kind), [days, kind]);
  const credits = useMemo(() => itemsCredits(needs.items), [needs]);
  const makesLine = useMemo(() => {
    if (needs.items.length === 0) {
      return null;
    }
    const names = [...new Set(needs.themeIds.map((themeId) => planAssetName(intl, themeId)))];
    return intl.formatMessage(planMessages.gapMakes, {
      assets: intl.formatList(names, { type: 'conjunction' }),
      count: names.length,
      credits,
    });
  }, [credits, intl, needs]);

  const { add } = drafts;
  const send = useCallback(
    (dayKeys: RolloutDayKey[], cost: number) => {
      if (dayKeys.length === 0 || !canSpend(cost)) {
        return;
      }
      add({ kind, platforms, dayKeys, makeMissing: true }).catch(console.error);
    },
    [add, canSpend, kind, platforms]
  );
  const addAll = useCallback(
    () =>
      send(
        days.map((day) => day.key as RolloutDayKey),
        credits
      ),
    [credits, days, send]
  );
  const addDay = useCallback(
    (day: ReleasePlanDay) => send([day.key as RolloutDayKey], itemsCredits(planMakeNeeds([day], kind).items)),
    [kind, send]
  );

  return { drafts, needs, credits, makesLine, addAll, addDay };
}
